import { useCallback, useState } from "react";
import { runValidators, hasErrors } from "../../lib/validation";

/** Minimal form hook — values, errors, touched, helpers.
 *
 * Usage:
 *   const form = useForm({
 *     initial: { name: "", email: "" },
 *     validators: { name: required, email: compose(required, emailValidator) },
 *   });
 *   <input {...form.field("name")} />
 *   form.errors.name // 'validation.required' or null
 *   await form.handleSubmit(async (values) => { ... });
 */
export function useForm({ initial, validators = {} }) {
    const [values, setValues] = useState(initial);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [submitting, setSubmitting] = useState(false);

    const set = useCallback((name, value) => {
        setValues((v) => ({ ...v, [name]: value }));
        // clear error for the field once edited
        setErrors((e) => (e[name] ? { ...e, [name]: null } : e));
    }, []);

    const setAll = useCallback((patch) => {
        setValues((v) => ({ ...v, ...patch }));
    }, []);

    const validateAll = useCallback(() => {
        const errs = runValidators(values, validators);
        setErrors(errs);
        setTouched(Object.keys(validators).reduce((acc, k) => ({ ...acc, [k]: true }), {}));
        return !hasErrors(errs);
    }, [values, validators]);

    const handleSubmit = useCallback(
        async (onValid, onInvalid) => {
            if (!validateAll()) {
                if (onInvalid) onInvalid();
                return false;
            }
            setSubmitting(true);
            try {
                await onValid(values);
                return true;
            } finally {
                setSubmitting(false);
            }
        },
        [validateAll, values],
    );

    const field = useCallback(
        (name) => ({
            value: values[name] ?? "",
            onChange: (e) =>
                set(name, e?.target?.type === "checkbox" ? e.target.checked : e?.target?.value ?? e),
            onBlur: () => setTouched((t) => ({ ...t, [name]: true })),
            name,
            id: name,
        }),
        [values, set],
    );

    return {
        values,
        setValues: setAll,
        set,
        errors,
        touched,
        submitting,
        setSubmitting,
        validateAll,
        handleSubmit,
        field,
        reset: (next) => {
            setValues(next ?? initial);
            setErrors({});
            setTouched({});
        },
    };
}
